import { AlertCircle, Hammer, Loader2, Sparkles } from 'lucide-react'
import { useAtomValue } from 'jotai'
import { Streamdown } from 'streamdown'
import type {
  ParamValidationDTO,
  RunbookDependencyDTO,
  RunbookStepDTO,
} from '@deepguide-ai/types'
import { buildableRunbookPlanAtom } from '@/stores/runbookDraftStore'
import {
  stepOutputs,
  type RunbookStepOutput,
} from '@/components/compliance/runbook-step-outputs'
import {
  RunbookRefPill,
  runbookRefStreamdownComponents,
} from './runbook-ref-pills'

export interface ProposedRunbookParam {
  name: string
  description: string | null
  type: string
  required: boolean
  defaultValue: string | null
  enumOptions: string[]
  validation: ParamValidationDTO | null
}

export interface ProposedRunbookDraft {
  proposalHash: string | null
  title: string
  description: string | null
  notes: string | null
  params: ProposedRunbookParam[]
  markdown: string
  /**
   * Structured steps (keyed on positional `step-N` ids) — each carries its
   * `kind` and, for human steps, an explicit `human` form. Persisted verbatim
   * via the structured-steps channel so the form survives (no markdown
   * re-compile / regex inference). `markdown` is kept only for preview.
   */
  steps: RunbookStepDTO[]
  /**
   * RFC-167 §13.6 — branch edges (keyed on positional `step-N` ids) when Vera
   * authored a non-linear procedure. Empty for a linear runbook; carried
   * through to `createRunbook` so the branches persist into
   * `Workflow.dependencies` instead of being flattened to a sequential chain.
   */
  dependencies: RunbookDependencyDTO[]
}

interface ToolPartProps {
  part: any
}

function getToolPayload(part: any): Record<string, unknown> | null {
  const candidate = part.input ?? part.args ?? part.output ?? part.result
  return candidate && typeof candidate === 'object' ? candidate : null
}

export function extractRunbookDraftFromToolPart(part: any): ProposedRunbookDraft | null {
  const rawOutput = part.output ?? part.result
  const output =
    typeof rawOutput === 'string' ? safeJsonParse(rawOutput) : rawOutput
  if (!output || typeof output !== 'object') return null
  const obj = output as Record<string, unknown>
  if (typeof obj.title !== 'string') return null
  if (typeof obj.markdown !== 'string' || obj.markdown.trim().length === 0) {
    return null
  }
  const params: ProposedRunbookParam[] = (Array.isArray(obj.params) ? obj.params : [])
    .map((entry): ProposedRunbookParam | null => {
      if (!entry || typeof entry !== 'object') return null
      const p = entry as Record<string, unknown>
      if (typeof p.name !== 'string' || p.name.trim().length === 0) return null
      return {
        name: p.name.trim(),
        description: typeof p.description === 'string' ? p.description : null,
        type: typeof p.type === 'string' ? p.type : 'string',
        required: typeof p.required === 'boolean' ? p.required : true,
        defaultValue: typeof p.defaultValue === 'string' ? p.defaultValue : null,
        enumOptions: Array.isArray(p.enumOptions)
          ? p.enumOptions.filter((v): v is string => typeof v === 'string')
          : [],
        validation:
          p.validation &&
          typeof p.validation === 'object' &&
          !Array.isArray(p.validation)
            ? (p.validation as ParamValidationDTO)
            : null,
      }
    })
    .filter((p): p is ProposedRunbookParam => p !== null)
  const dependencies: RunbookDependencyDTO[] = (
    Array.isArray(obj.dependencies) ? obj.dependencies : []
  ).filter(
    (entry): entry is RunbookDependencyDTO =>
      !!entry &&
      typeof entry === 'object' &&
      typeof (entry as Record<string, unknown>).fromStepId === 'string' &&
      typeof (entry as Record<string, unknown>).toStepId === 'string',
  )
  const steps: RunbookStepDTO[] = (
    Array.isArray(obj.steps) ? obj.steps : []
  ).filter(
    (entry): entry is RunbookStepDTO =>
      !!entry &&
      typeof entry === 'object' &&
      typeof (entry as Record<string, unknown>).id === 'string',
  )
  return {
    proposalHash:
      typeof obj.proposalHash === 'string' ? obj.proposalHash : null,
    title: obj.title,
    description: typeof obj.description === 'string' ? obj.description : null,
    notes: typeof obj.notes === 'string' ? obj.notes : null,
    params,
    markdown: obj.markdown,
    steps,
    dependencies,
  }
}

function safeJsonParse(value: string): unknown {
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

function getToolPartError(part: any): string | null {
  if (part?.state === 'output-error' || part?.state === 'tool-error') {
    return (
      part?.errorText ||
      part?.error?.message ||
      (typeof part?.error === 'string' ? part.error : null) ||
      'Tool call failed'
    )
  }
  const result = part?.output ?? part?.result
  if (result && typeof result === 'object' && (result as any).ok === false) {
    const msg = (result as any).error
    return typeof msg === 'string' ? msg : 'Tool call failed'
  }
  return null
}

function stepDescription(step: RunbookStepDTO): string {
  // The full instruction prose IS the step's description — show it untruncated.
  // `title` is a short, server-truncated label (often ends in "…"), so only
  // fall back to it when there's no instruction.
  return step.instruction?.trim() || step.title?.trim() || 'Untitled step'
}

/**
 * The declared inputs the procedure asks for at run time. Without this the
 * `@param` pills in the step prose are indistinguishable from step-output pills
 * (the alias drops the scope), so the reader can't tell what they'd be prompted
 * for before the procedure runs.
 */
function PlanInputsRow({ params }: { params: ProposedRunbookParam[] }) {
  return (
    <div className='border-t border-border px-5 py-4'>
      <div className='text-[11px] font-medium tracking-wide text-text-subtle uppercase'>
        Inputs
      </div>
      <ul className='mt-2 flex flex-col gap-1.5'>
        {params.map((param) => (
          <li
            key={param.name}
            className='flex flex-wrap items-baseline gap-x-2 gap-y-1 text-[13px] text-text-secondary'
          >
            <RunbookRefPill refValue={`@params.${param.name}`} />
            <span className='text-text-subtle'>
              {param.type}
              {!param.required && ' · optional'}
              {param.defaultValue && ` · default ${param.defaultValue}`}
            </span>
            {param.description && <span>{param.description}</span>}
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * What this step produces, as ref pills. Counterpart to `PlanInputsRow`: an
 * `@expectedAccess` pill in a later step's prose is otherwise unattributable —
 * the alias drops the scope, so it reads identically to a declared input. This
 * row is where the reader sees which step minted it.
 */
function PlanStepOutputsRow({ outputs }: { outputs: RunbookStepOutput[] }) {
  return (
    <div className='mt-1.5 flex flex-wrap items-baseline gap-x-2 gap-y-1'>
      <span className='text-[10px] font-medium tracking-wide text-text-subtle uppercase'>
        Produces
      </span>
      {outputs.map((output) => (
        <RunbookRefPill key={output.ref} refValue={output.ref} />
      ))}
    </div>
  )
}

function PlanStepRow({ step }: { step: RunbookStepDTO }) {
  const outputs = stepOutputs(step)
  return (
    <li className='min-w-0 pl-1'>
      <Streamdown
        className='min-w-0 flex-1 break-words text-[13px] leading-[1.65] text-text-secondary [&>*:first-child]:mt-0 [&>*:last-child]:mb-0 [&_li]:my-0 [&_ol]:my-1 [&_ol]:pl-4 [&_p]:my-0 [&_ul]:my-1 [&_ul]:pl-4'
        components={runbookRefStreamdownComponents}
      >
        {stepDescription(step)}
      </Streamdown>
      {outputs.length > 0 && <PlanStepOutputsRow outputs={outputs} />}
    </li>
  )
}

export function ProposeRunbookMessage({ part }: ToolPartProps) {
  const { callId: buildableCallId, building } = useAtomValue(
    buildableRunbookPlanAtom,
  )
  const args = getToolPayload(part)
  const draft = extractRunbookDraftFromToolPart(part)
  const title = draft?.title ?? (typeof args?.title === 'string' ? args.title : undefined)
  const steps = draft?.steps ?? []
  const params = draft?.params ?? []
  // RFC-167 §13.6 — surface that a procedure is branched. Prefer the failure-
  // fork count (the meaningful branch signal); fall back to a generic
  // "branched" label for a success-only non-linear graph (parallel / fan-in),
  // which still persists with viewPreference:'graph'. Works for both the input
  // (`condition`) and output dependency shapes.
  const deps =
    draft?.dependencies ?? (Array.isArray(args?.dependencies) ? args.dependencies : [])
  const failureBranches = deps.filter(
    (dep) =>
      !!dep &&
      typeof dep === 'object' &&
      (dep as Record<string, unknown>).condition === 'failure',
  ).length
  let branchLabel = ''
  if (failureBranches > 0) {
    branchLabel = ` · ${failureBranches} branch${failureBranches === 1 ? '' : 'es'}`
  } else if (deps.length > 0) {
    branchLabel = ' · branched'
  }
  const errorMessage = getToolPartError(part)
  const complete = Boolean(part.output ?? part.result)
  // Offer Build on any complete plan card while a drafting page is active
  // (`buildableCallId != null` — set only by /runbooks/new). Clicking always
  // builds the LATEST staged plan, so we don't gate on an exact call-id match
  // (that race left the newest card without a button). Other surfaces never
  // set the atom, so no stray Build button appears there.
  const canBuild = complete && buildableCallId != null

  if (errorMessage) {
    return (
      <div className='rounded-md border border-status-red-border-subtle bg-status-red-surface-subtle px-3 py-2 text-[12px]'>
        <div className='flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-status-red-strong uppercase'>
          <AlertCircle className='h-3 w-3' />
          Plan failed
        </div>
        {title && (
          <div className='mt-1 text-[12px] text-text-secondary'>
            <strong>{title}</strong>
          </div>
        )}
        <div className='mt-1 text-[11px] text-status-red-ink'>{errorMessage}</div>
        <div className='mt-1 text-[11px] text-text-subtle'>
          Ask Vera to retry. Nothing was saved.
        </div>
      </div>
    )
  }

  return (
    <div className='rounded-md border border-border bg-card text-[12px]'>
      <div className='px-5 py-4'>
        <div className='flex items-center gap-1.5 text-[11px] font-medium tracking-wide text-text-subtle uppercase'>
          {complete ? (
            <Sparkles className='h-3 w-3' />
          ) : (
            <Loader2 className='h-3 w-3 animate-spin' />
          )}
          {complete ? 'Procedure plan' : 'Planning...'}
        </div>
        {(title || steps.length > 0) && (
          <div className='mt-1 text-[13px] text-text-secondary'>
            {title && <strong>{title}</strong>}
            {title && steps.length > 0 && ' - '}
            {steps.length > 0 && `${steps.length} step${steps.length === 1 ? '' : 's'}`}
            {branchLabel}
          </div>
        )}
        {steps.length === 0 && (
          <div className='mt-1 text-[11px] text-text-subtle'>
            {complete ? 'No steps in this plan.' : 'Building the plan...'}
          </div>
        )}
      </div>
      {params.length > 0 && <PlanInputsRow params={params} />}
      {steps.length > 0 && (
        <ol className='flex list-decimal flex-col gap-3 border-t border-border px-5 py-4 pl-10 text-[13px] marker:text-foreground'>
          {steps.map((step) => (
            <PlanStepRow key={step.id} step={step} />
          ))}
        </ol>
      )}
      {canBuild && (
        <div className='flex items-center justify-between gap-2 border-t border-border px-5 py-3'>
          <span className='text-[10px] text-text-subtle'>
            Review the steps, then build. Or ask Vera for changes.
          </span>
          <button
            type='button'
            disabled={building}
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent('screenata:build-runbook', {
                  detail: { toolCallId: part.toolCallId },
                }),
              )
            }
            className='inline-flex shrink-0 items-center gap-1.5 rounded-md bg-primary px-2.5 py-1 text-[11px] font-medium text-primary-foreground transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-60'
          >
            {building ? (
              <Loader2 className='h-3 w-3 animate-spin' />
            ) : (
              <Hammer className='h-3 w-3' />
            )}
            {building ? 'Building…' : 'Build procedure'}
          </button>
        </div>
      )}
    </div>
  )
}
